/**
 * Transport bar (04 Transport and time control): play/pause, event and lap jumps, Director and
 * free rates, the scrub bar, readouts (race clock · LAP x/53 · speed), follow dropdown, view
 * reset, fullscreen and help (right end).
 *
 * Portrait (r14 P8, `setPortrait`): one row at the top of the bottom sheet — ▶, the scrub, a rate
 * cycle (1× / 5× / 20× / 60×) and `⋯`, a menu with FOLLOW, DIRECTOR, zoom −/+, CC, SND, LITE, `?`
 * and fullscreen where the browser has it. The menu borrows the landscape bar's own controls (they
 * move back on a turn to landscape, so the landscape bar is unchanged); the rest of the landscape
 * bar hides (CSS, `#app[data-portrait='1']`).
 */
import type { Mode, RGB } from '../core/api.ts';
import type { LiteSetting } from '../core/layout/lite.ts';
import type { PixelFonts } from '../core/hud/font.ts';
import { type ChromeScale, PixelLabel } from './pixel-label.ts';
import type { ScrubBar } from './scrubbar.ts';

export interface TransportActions {
  togglePlay(): void;
  setMode(m: Mode): void;
  jumpEvent(dir: -1 | 1): void;
  jumpLap(dir: -1 | 1): void;
  follow(car: number | null): void;
  resetView(): void;
  toggleFullscreen(): void;
  toggleHelp(): void;
  toggleSound(): void;
  toggleCaptions(): void;
  /** the `+` / `-` keys (portrait menu) */
  zoom(factor: number): void;
  /** LITE Auto → On → Off (portrait menu; the help card has the same choice) */
  cycleLite(): void;
}

export interface DriverOption {
  idx: number;
  code: string;
  name: string;
  team: string;
  hex: string;
}

export interface TransportReadout {
  playing: boolean;
  mode: Mode;
  clock: string;
  lap: number;
  laps: number;
  speed: string;
}

const MODES: { label: string; mode: Mode }[] = [
  { label: 'DIRECTOR', mode: { kind: 'director' } },
  { label: '1×', mode: { kind: 'free', rate: 1 } },
  { label: '5×', mode: { kind: 'free', rate: 5 } },
  { label: '20×', mode: { kind: 'free', rate: 20 } },
  { label: '60×', mode: { kind: 'free', rate: 60 } },
];

function modeKey(m: Mode): string {
  return m.kind === 'director' ? 'director' : String(m.rate);
}

/** The portrait rate cycle: Director → 1×, then 1× → 5× → 20× → 60× → 1×. */
export function nextRate(m: Mode): Mode {
  const rates = [1, 5, 20, 60] as const;
  if (m.kind === 'director') return { kind: 'free', rate: 1 };
  return { kind: 'free', rate: rates[(rates.indexOf(m.rate) + 1) % rates.length] ?? 1 };
}

const LITE_LABEL: Readonly<Record<LiteSetting, string>> = { auto: 'LITE AUTO', on: 'LITE ON', off: 'LITE OFF' };

const TEXT: RGB = [232, 230, 223];
const DIM: RGB = [150, 152, 160];

export class Transport {
  readonly el: HTMLDivElement;
  private readonly playBtn: HTMLButtonElement;
  private readonly modeBtns = new Map<string, HTMLButtonElement>();
  private readonly clock = new PixelLabel('L', 'px-label readout readout-clock');
  private readonly lap = new PixelLabel('L', 'px-label readout readout-lap');
  private readonly speed = new PixelLabel('L', 'px-label readout readout-speed');
  /** credit line inside the bar (overlay mode: the bar covers the map's own credit) */
  private readonly attribution = new PixelLabel('S', 'px-label attr-label');
  private attrText = '';
  private readonly live: HTMLSpanElement;
  private liveNext = 0;
  private readonly driverOpts = new Map<number, { el: HTMLOptionElement; text: string }>();
  private retiredKey = '';
  private readonly followSel: HTMLSelectElement;
  private readonly soundBtn: HTMLButtonElement;
  private readonly ccBtn: HTMLButtonElement;
  private readonly resetBtn: HTMLButtonElement;
  private readonly fsBtn: HTMLButtonElement;
  private readonly helpBtn: HTMLButtonElement;
  /** portrait only: the rate cycle, `⋯` and its menu (hidden in landscape) */
  private readonly rateBtn: HTMLButtonElement;
  private readonly moreBtn: HTMLButtonElement;
  private readonly menu: HTMLDivElement;
  private readonly liteBtn: HTMLButtonElement;
  private readonly dirBtn: HTMLButtonElement;
  private readonly zoomBtns: HTMLButtonElement[];
  private portrait = false;
  private fonts: PixelFonts | null = null;
  private sc: ChromeScale = { k: 1, dpr: 1 };
  private last: TransportReadout | null = null;

  constructor(
    private readonly actions: TransportActions,
    scrub: ScrubBar,
    drivers: readonly DriverOption[],
  ) {
    this.el = document.createElement('div');
    this.el.id = 'transport';
    this.el.className = 'chrome-bar';

    this.playBtn = this.button('btn-play', '▶', 'Play / pause (Space)', () => {
      actions.togglePlay();
    });
    this.playBtn.classList.add('play');
    this.playBtn.setAttribute('aria-pressed', 'false');
    this.el.appendChild(this.playBtn);

    const jumps = document.createElement('div');
    jumps.className = 'group';
    jumps.append(
      this.button('btn-prev-ev', '⏮', 'Previous event (,)', () => {
        actions.jumpEvent(-1);
      }),
      this.button('btn-prev-lap', '⏪', 'Previous lap (Shift+←)', () => {
        actions.jumpLap(-1);
      }),
      this.button('btn-next-lap', '⏩', 'Next lap (Shift+→)', () => {
        actions.jumpLap(1);
      }),
      this.button('btn-next-ev', '⏭', 'Next event (.)', () => {
        actions.jumpEvent(1);
      }),
    );
    this.el.appendChild(jumps);

    const modes = document.createElement('div');
    modes.className = 'group segmented';
    modes.setAttribute('role', 'radiogroup');
    for (const m of MODES) {
      const b = this.button(`btn-mode-${modeKey(m.mode)}`, m.label, m.mode.kind === 'director' ? 'Director: the Director time warp (D)' : `Free rate ${m.label} ([ / ])`, () => {
        actions.setMode(m.mode);
      });
      b.classList.add('mode');
      b.setAttribute('role', 'radio');
      b.setAttribute('aria-checked', 'false');
      b.dataset.mode = modeKey(m.mode);
      modes.appendChild(b);
      this.modeBtns.set(modeKey(m.mode), b);
    }
    this.el.appendChild(modes);

    this.el.appendChild(scrub.el);

    const read = document.createElement('div');
    read.className = 'group readouts';
    this.lap.el.classList.add('lap-readout');
    read.append(this.clock.el, this.lap.el, this.speed.el);
    this.el.appendChild(read);
    this.live = document.createElement('span');
    this.live.className = 'sr-only';
    this.live.setAttribute('aria-live', 'polite');
    this.el.appendChild(this.live);
    this.attribution.el.setAttribute('role', 'note');
    this.el.appendChild(this.attribution.el);

    this.followSel = document.createElement('select');
    this.followSel.id = 'follow-select';
    this.followSel.title = 'Follow a car (click a car or tower row; Esc releases)';
    const none = document.createElement('option');
    none.value = '';
    none.textContent = 'FOLLOW —';
    this.followSel.appendChild(none);
    for (const d of drivers) {
      const o = document.createElement('option');
      o.value = String(d.idx);
      o.textContent = `${d.code}  ${d.name}`;
      this.followSel.appendChild(o);
      this.driverOpts.set(d.idx, { el: o, text: o.textContent });
    }
    this.followSel.addEventListener('change', () => {
      const v = this.followSel.value;
      actions.follow(v === '' ? null : Number(v));
      this.followSel.blur();
    });
    this.el.appendChild(this.followSel);

    // text toggles (not glyphs: an emoji speaker would differ per platform); both reflect state in aria-pressed
    this.soundBtn = this.button('btn-sound', 'SND', 'Sound on / off (M)', () => {
      actions.toggleSound();
    });
    this.ccBtn = this.button('btn-cc', 'CC', 'Captions on / off (C)', () => {
      actions.toggleCaptions();
    });
    for (const b of [this.soundBtn, this.ccBtn]) {
      b.classList.add('toggle');
      this.el.appendChild(b);
    }
    this.setSound(false);
    this.setCaptions(true);

    this.resetBtn = this.button('btn-reset-view', '⟲', 'Reset the view (R)', () => {
      actions.resetView();
    });
    this.fsBtn = this.button('btn-fullscreen', '⛶', 'Fullscreen (F)', () => {
      actions.toggleFullscreen();
    });
    // P-2: iOS Safari (iPhone) exposes no Fullscreen API for documents: hide ⛶ there — the ?
    // help suggests Share → Add to Home Screen instead
    this.fsBtn.hidden = !document.fullscreenEnabled;
    this.helpBtn = this.button('btn-help', '?', 'Help (?)', () => {
      actions.toggleHelp();
    });
    this.el.append(this.resetBtn, this.fsBtn, this.helpBtn);

    // portrait: the rate cycle and `⋯` follow the scrub; the menu opens over the sheet or the map
    this.rateBtn = this.button('btn-rate', '1×', 'Rate: 1×, 5×, 20×, 60× ([ / ])', () => {
      actions.setMode(nextRate(this.last?.mode ?? { kind: 'director' }));
    });
    this.rateBtn.classList.add('portrait-only', 'rate');
    this.moreBtn = this.button('btn-more', '⋯', 'More controls', () => {
      this.setMenu(this.moreBtn.getAttribute('aria-expanded') !== 'true');
    });
    this.moreBtn.classList.add('portrait-only');
    this.moreBtn.setAttribute('aria-expanded', 'false');
    this.menu = document.createElement('div');
    this.menu.id = 'transport-menu';
    this.menu.hidden = true;
    this.menu.setAttribute('role', 'menu');
    this.liteBtn = this.button('btn-lite', LITE_LABEL.auto, 'LITE: Auto, On or Off', () => {
      actions.cycleLite();
    });
    this.dirBtn = this.button('btn-director', 'DIRECTOR', 'Director: the Director time warp (D)', () => {
      actions.setMode({ kind: 'director' });
    });
    this.dirBtn.classList.add('toggle');
    this.zoomBtns = [
      this.button('btn-zoom-out', '−', 'Zoom out (-)', () => {
        actions.zoom(1 / 1.15);
      }),
      this.button('btn-zoom-in', '+', 'Zoom in (+)', () => {
        actions.zoom(1.15);
      }),
    ];
    this.menu.addEventListener('click', (e) => {
      // a tap on a menu button closes the menu (the FOLLOW select closes it on change)
      if ((e.target as HTMLElement).closest('button')) this.setMenu(false);
    });
    this.followSel.addEventListener('change', () => {
      this.setMenu(false);
    });
    this.el.append(this.rateBtn, this.moreBtn, this.menu);
  }

  /** Portrait: borrow the landscape bar's controls into the `⋯` menu; landscape: put them back in their order. */
  setPortrait(on: boolean): void {
    if (on === this.portrait) return;
    this.portrait = on;
    this.setMenu(false);
    if (on) {
      this.menu.append(this.followSel, this.dirBtn, ...this.zoomBtns, this.ccBtn, this.soundBtn, this.liteBtn, this.helpBtn);
      // fullscreen only where the browser has it (not on iPhone)
      if (document.fullscreenEnabled) this.menu.append(this.fsBtn);
    } else {
      for (const b of [this.followSel, this.soundBtn, this.ccBtn, this.resetBtn, this.fsBtn, this.helpBtn]) this.el.insertBefore(b, this.rateBtn);
    }
  }

  private setMenu(open: boolean): void {
    this.menu.hidden = !open;
    this.moreBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  setLite(s: LiteSetting): void {
    this.liteBtn.textContent = LITE_LABEL[s];
  }

  private button(id: string, text: string, title: string, onClick: () => void): HTMLButtonElement {
    const b = document.createElement('button');
    b.type = 'button';
    b.id = id;
    b.className = 'btn';
    b.textContent = text;
    b.title = title;
    b.setAttribute('aria-label', title);
    b.addEventListener('click', (e) => {
      onClick();
      (e.currentTarget as HTMLButtonElement).blur(); // keep keyboard shortcuts on the document
    });
    return b;
  }

  setFonts(f: PixelFonts | null): void {
    this.fonts = f;
    this.last = null;
    if (this.attrText) this.setAttribution(this.attrText);
  }

  setScale(sc: ChromeScale): void {
    this.sc = sc;
    this.last = null;
    if (this.attrText) this.setAttribution(this.attrText);
  }

  setAttribution(text: string): void {
    this.attrText = text;
    this.attribution.set(text, [150, 154, 166], this.fonts, this.sc);
  }

  /** Mark retired cars OUT (not followable) in the dropdown; `mask` bit i = car i retired. */
  setRetired(mask: number): void {
    const key = String(mask);
    if (key === this.retiredKey) return;
    this.retiredKey = key;
    for (const [idx, o] of this.driverOpts) {
      const out = (mask & (1 << idx)) !== 0;
      o.el.disabled = out;
      o.el.textContent = out ? `${o.text}  OUT` : o.text;
    }
  }

  setSound(on: boolean): void {
    this.soundBtn.classList.toggle('active', on);
    this.soundBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
  }

  setCaptions(on: boolean): void {
    this.ccBtn.classList.toggle('active', on);
    this.ccBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
  }

  setFollow(car: number | null): void {
    const v = car === null ? '' : String(car);
    if (this.followSel.value !== v) this.followSel.value = v;
  }

  /** Per frame; touches the DOM only for changed values. */
  update(r: TransportReadout): void {
    const l = this.last;
    if (!l || l.playing !== r.playing) {
      this.playBtn.textContent = r.playing ? '❚❚' : '▶';
      this.playBtn.classList.toggle('playing', r.playing);
      this.playBtn.setAttribute('aria-pressed', r.playing ? 'true' : 'false');
      document.documentElement.dataset.playing = r.playing ? '1' : '0';
    }
    if (!l || modeKey(l.mode) !== modeKey(r.mode)) {
      const k = modeKey(r.mode);
      for (const [key, b] of this.modeBtns) {
        b.classList.toggle('active', key === k);
        b.setAttribute('aria-checked', key === k ? 'true' : 'false');
      }
      this.rateBtn.textContent = r.mode.kind === 'director' ? 'DIR' : `${String(r.mode.rate)}×`;
      this.dirBtn.classList.toggle('active', r.mode.kind === 'director');
    }
    if (!l || l.clock !== r.clock) this.clock.set(r.clock, TEXT, this.fonts, this.sc);
    if (!l || l.lap !== r.lap || l.laps !== r.laps) this.lap.set(`LAP ${String(r.lap)}/${String(r.laps)}`, TEXT, this.fonts, this.sc);
    if (!l || l.speed !== r.speed) this.speed.set(r.speed, r.speed === 'PAUSE' || r.speed === 'BUFFERING' ? DIM : TEXT, this.fonts, this.sc);
    this.last = { ...r };
    // screen readers: a calm summary at most every 5 s
    const now = performance.now();
    if (now >= this.liveNext) {
      this.liveNext = now + 5000;
      this.live.textContent = `${r.clock}, lap ${String(r.lap)} of ${String(r.laps)}, ${r.speed}`;
    }
  }
}
